// UTM capture + Hotmart checkout URL builder + Meta Pixel.
//
// Responsibilities:
//   1. Capture UTM params from the landing URL once and persist them in
//      sessionStorage so they survive the funnel flow.
//   2. Build a Hotmart checkout URL that forwards UTM params plus a
//      pipe-joined sck/src identifier for native Hotmart attribution.
//   3. Initialize the Meta Pixel and expose typed helpers for PageView /
//      ViewContent / standard event tracking.
//   4. Be fault-tolerant — tracking errors must NEVER block redirects.

declare global {
  interface Window {
    fbq?: (...args: any[]) => void
    _fbq?: any
  }
}

const PIXEL_ID = '958699360076044'
const STORAGE_KEY = 'tphlanding_utms_v1'
const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const

type UtmKey = (typeof UTM_KEYS)[number]
type TrackingData = Partial<Record<UtmKey, string>> & {
  landingUrl?: string
  landedAt?: string
}

// ---------------------------------------------------------------------------
// Init — capture landing UTMs + boot Meta Pixel
// ---------------------------------------------------------------------------
let initialized = false

export function initTracking() {
  if (typeof window === 'undefined' || initialized) return
  initialized = true

  try {
    captureLandingParams()
  } catch (err) {
    console.warn('[tracking] captureLandingParams failed', err)
  }

  try {
    initMetaPixel()
  } catch (err) {
    console.warn('[tracking] initMetaPixel failed', err)
  }
}

function initMetaPixel() {
  if (typeof window === 'undefined') return // Standard Meta Pixel base code
  ;(function (f: any, b: Document, e: string, v: string) {
    if (f.fbq) return
    const n: any = (f.fbq = function () {
      // eslint-disable-next-line prefer-rest-params
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments)
    })
    if (!f._fbq) f._fbq = n
    n.push = n
    n.loaded = true
    n.version = '2.0'
    n.queue = []
    const t = b.createElement(e) as HTMLScriptElement
    t.async = true
    t.src = v
    const s = b.getElementsByTagName(e)[0]
    if (s && s.parentNode) {
      s.parentNode.insertBefore(t, s)
    } else {
      ;(b.head || b.documentElement).appendChild(t)
    }
  })(
    window,
    document,
    'script',
    'https://connect.facebook.net/en_US/fbevents.js',
  )

  if (window.fbq) {
    window.fbq('init', PIXEL_ID)
    window.fbq('track', 'PageView')
  }

  // <noscript> fallback pixel image
  if (!document.getElementById('fb-pixel-noscript')) {
    const noscript = document.createElement('noscript')
    noscript.id = 'fb-pixel-noscript'
    const img = document.createElement('img')
    img.height = 1
    img.width = 1
    img.style.display = 'none'
    img.src = `https://www.facebook.com/tr?id=${PIXEL_ID}&ev=PageView&noscript=1`
    noscript.appendChild(img)
    document.body.appendChild(noscript)
  }
}

// ---------------------------------------------------------------------------
// Event helpers
// ---------------------------------------------------------------------------
export function trackPageView() {
  try {
    if (typeof window === 'undefined' || !window.fbq) return
    window.fbq('track', 'PageView')
  } catch (err) {
    console.warn('[tracking] trackPageView failed', err)
  }
}

export function trackEvent(name: string, params?: Record<string, any>) {
  try {
    if (typeof window === 'undefined' || !window.fbq) return
    const standardEvents = new Set([
      'ViewContent',
      'Search',
      'AddToCart',
      'AddToWishlist',
      'InitiateCheckout',
      'AddPaymentInfo',
      'Purchase',
      'Lead',
      'CompleteRegistration',
      'Contact',
      'CustomizeProduct',
      'Donate',
      'FindLocation',
      'Schedule',
      'StartTrial',
      'SubmitApplication',
      'Subscribe',
    ])
    // Always enrich every Pixel event with the captured UTM params so Meta
    // Ads Manager can attribute back to the originating campaign — even
    // before the user reaches the Hotmart checkout.
    const enriched = { ...getUtmEventParams(), ...params }
    if (standardEvents.has(name)) {
      window.fbq('track', name, enriched)
    } else {
      window.fbq('trackCustom', name, enriched)
    }
  } catch (err) {
    console.warn('[tracking] trackEvent failed', err)
  }
}

// ---------------------------------------------------------------------------
// URL + UTM helpers
// ---------------------------------------------------------------------------
function getUrlParam(name: string): string | null {
  if (typeof window === 'undefined') return null
  try {
    return new URLSearchParams(window.location.search).get(name)
  } catch {
    return null
  }
}

function captureLandingParams() {
  if (typeof window === 'undefined') return
  const stored = getStoredTracking()

  let hasNew = false
  const next: TrackingData = { ...stored }

  UTM_KEYS.forEach((key) => {
    const v = getUrlParam(key)
    if (v) {
      next[key] = v
      hasNew = true
    }
  })

  if (hasNew || !stored.landedAt) {
    next.landingUrl = window.location.href
    next.landedAt = new Date().toISOString()
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next))
    } catch {
      // sessionStorage may be unavailable (private mode); ignore
    }
  }
}

function getStoredTracking(): TrackingData {
  if (typeof window === 'undefined') return {}
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    return raw ? (JSON.parse(raw) as TrackingData) : {}
  } catch {
    return {}
  }
}

/**
 * Get UTM params formatted for inclusion in Meta Pixel event payloads.
 * Empty object if no UTMs were captured (organic traffic).
 */
export function getUtmEventParams(): Record<string, string> {
  const tracking = getStoredTracking()
  const params: Record<string, string> = {}
  UTM_KEYS.forEach((key) => {
    if (tracking[key]) params[key] = tracking[key] as string
  })
  return params
}

// ---------------------------------------------------------------------------
// Hotmart checkout URL builder
// ---------------------------------------------------------------------------
export function buildCheckoutUrl(baseUrl: string): string {
  try {
    const tracking = getStoredTracking()
    const url = new URL(baseUrl)

    UTM_KEYS.forEach((key) => {
      if (tracking[key]) {
        url.searchParams.set(key, tracking[key] as string)
      }
    })

    const combinedParts = UTM_KEYS.map((k) => tracking[k]).filter(
      (v): v is string => Boolean(v),
    )
    const combined =
      combinedParts.length > 0
        ? combinedParts.join('|')
        : 'organico|protocolo-7-dias'

    url.searchParams.set('sck', combined)
    url.searchParams.set('src', combined)

    return url.toString()
  } catch (err) {
    console.warn('[tracking] buildCheckoutUrl failed, returning base URL', err)
    return baseUrl
  }
}
